/* eslint-disable no-empty */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import IMask from "imask";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import getFirstAndLastName from "../../../utils/helper";
import "./TwoStepOrderForm.scss";


const DEFAULT_REDIRECT_PROD = "https://app.unclekam.com/thankyou";
const DEFAULT_REDIRECT_STAGING = "https://staging.unclekam.com/thankyou";

const stripeInputStyle = {
  style: {
    base: {
      fontSize: "14px",
      color: "#212529",
      "::placeholder": { color: "#ADB5BD" },
      fontFamily:
        'Open Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
    },
    invalid: { color: "#dc3545" },
  },
};

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const parseSubscription = (val) => {
  if (!val) return {};
  try { return typeof val === "string" ? JSON.parse(val) : val; }
  catch {
    try { return JSON.parse(String(val)); }
    catch { return {}; }
  }
};

function getEffectivePrice(p) {
  const d = Number.parseInt(String(p?.discounted_price ?? "").trim(), 10);
  const base = Number.parseInt(String(p?.price ?? "0").trim(), 10) || 0;
  return Number.isFinite(d) && d > 0 ? d : base;
}
function hasRealDiscount(p) {
  const base = Number.parseInt(String(p?.price ?? "0").trim(), 10) || 0;
  const eff = getEffectivePrice(p);
  return eff > 0 && base > 0 && eff < base;
}

function TwoStepOrderFormInner({
  productIds,
  additionalProductIds,
  redirectUrl,
  qa_mode,
  proxyUrl,
}) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(
    () => (typeof qa_mode === "boolean" ? qa_mode : params.get("qa_mode") === "on"),
    [qa_mode, params]
  );

  const DEFAULT_REDIRECT = qa ? DEFAULT_REDIRECT_STAGING : DEFAULT_REDIRECT_PROD;

  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const [step, setStep] = useState(1);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  const phoneRef = useRef(null);
  const phoneMaskRef = useRef(null);
  const [phone, setPhone] = useState("");

  const [products, setProducts] = useState([]);
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [selectedMain, setSelectedMain] = useState(new Set());
  const [selectedUpsell, setSelectedUpsell] = useState(new Set());
  const [productIdsState, setProductIdsState] = useState([]);

  const [alert, setAlert] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!phoneRef.current) return;
    const m = IMask(phoneRef.current, {
      mask: [
        {
          mask: "(000) 000-0000",
          country: "US",
        },
        {
          mask: "+1 (000) 000-0000",
          country: "US_INTL",
        },
        {
          mask: "+995 (000) 000-000",
          country: "GE",
        },
        {
          mask: "+00000000000000",
          country: "INTL",
        },
      ],
      dispatch: (appended, dynamicMasked) => {
        const raw = dynamicMasked.value + appended;
        if (raw.startsWith("+1")) {
          return dynamicMasked.compiledMasks.find((m) => m.country === "US_INTL");
        }
        if (raw.startsWith("+995")) {
          return dynamicMasked.compiledMasks.find((m) => m.country === "GE");
        }
        if (raw.startsWith("+")) {
          return dynamicMasked.compiledMasks.find((m) => m.country === "INTL");
        }
        return dynamicMasked.compiledMasks.find((m) => m.country === "US");
      },
    });
    m.on("accept", () => setPhone(m.value));
    phoneMaskRef.current = m;
    return () => m.destroy();
  }, []);

  useEffect(() => {
    const mainIds = (Array.isArray(productIds) ? productIds : String(productIds || "").split(","))
      .filter(Boolean)
      .map(Number);
    const upsellIds = (Array.isArray(additionalProductIds)
      ? additionalProductIds
      : String(additionalProductIds || "").split(","))
      .filter(Boolean)
      .map(Number);

    const all = [...mainIds, ...upsellIds];
    if (!all.length) return;

    let cancelled = false;
    (async () => {
      try {
        const idsParam = all.join(",");
        const { data: payload } = await axios.post(
          PROXY_URL,
          {
            path: `/get-products/${idsParam}`,
            method: "GET",
            qa_mode: qa,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const list =
          Array.isArray(payload) ? payload :
          Array.isArray(payload?.data) ? payload.data : [];

        if (cancelled) return;

        const mainSet = new Set(mainIds);
        const upsellSet = new Set(upsellIds);

        setProducts(list.filter((p) => mainSet.has(Number(p.id))));
        setAdditionalProducts(list.filter((p) => upsellSet.has(Number(p.id))));
      } catch {
        if (!cancelled) {
          setProducts([]);
          setAdditionalProducts([]);
          setAlert({ type: "error", message: "Failed to load products." });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [productIds, additionalProductIds, PROXY_URL, qa]);

  useEffect(() => {
    const ids = [
      ...Array.from(selectedMain.values()),
      ...Array.from(selectedUpsell.values()),
    ].map(Number);
    setProductIdsState(ids);
  }, [selectedMain, selectedUpsell]);

  const totalPrice = useMemo(() => {
    let sum = 0;
    for (const p of products) {
      if (selectedMain.has(p.id)) sum += getEffectivePrice(p);
    }
    for (const p of additionalProducts) {
      if (selectedUpsell.has(p.id)) sum += getEffectivePrice(p);
    }
    return sum;
  }, [products, additionalProducts, selectedMain, selectedUpsell]);

  const toggleMain = (id) =>
    setSelectedMain((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleUpsell = (id) =>
    setSelectedUpsell((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const goStep2 = () => {
    const digits = (phone || "").replace(/\D/g, "");
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const ok = fullName.trim().length > 0 && emailOk && digits.length >= 9;
    if (!ok) { setAlert({ type: "error", message: "Please fill out all required fields." }); return; }
    setAlert({ type: "", message: "" });
    setStep(2);
  };

  const clearAll = () => {
    setFullName("");
    setEmail("");
    setPhone("");
    setSelectedMain(new Set());
    setSelectedUpsell(new Set());
    setProductIdsState([]);
    const n = elements?.getElement(CardNumberElement);
    const e = elements?.getElement(CardExpiryElement);
    const c = elements?.getElement(CardCvcElement);
    n && n.clear(); e && e.clear(); c && c.clear();
    if (phoneMaskRef.current) phoneMaskRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setAlert({ type: "", message: "" });

    if ((!products.length && !additionalProducts.length) || (!selectedMain.size && !selectedUpsell.size)) {
      setAlert({ type: "error", message: "No Product Selected" });
      return;
    }
    if (!stripe || !elements) {
      setAlert({ type: "error", message: "Stripe is not ready yet." });
      return;
    }

    try {
      setSubmitting(true);

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
      });
      if (error) {
        setSubmitting(false);
        setAlert({ type: "error", message: error.message || "Payment error." });
        return;
      }

      const firstSelectedMainId = [...selectedMain][0] ?? products[0]?.id;
      const firstProduct = products.find((p) => p.id === firstSelectedMainId) || products[0];
      const subscription = parseSubscription(firstProduct?.subscription);
      const { firstName, lastName } = getFirstAndLastName(fullName || "");
      const roleId = Number(firstProduct?.role_id) || undefined;
      const cleanPhone = (phone || "").replace(/\D/g, "");

      const orderData = {
        firstname: firstName,
        lastname: lastName,
        email,
        phone: cleanPhone,
        role_id: roleId,
        invoice_name: "Unclekam Product",
        amount: totalPrice,
        cardData: paymentMethod,
        productIds: productIdsState,
      };

      if (String(subscription?.is_subscription) === "true") {
        orderData.subscription = {
          is_subscription: subscription?.is_subscription,
          trial_dates: subscription?.trial_dates,
          type_of_subscription: subscription?.type_of_subscription,
        };
      }

      const response = await axios.post(
        PROXY_URL,
        {
          path: "/pay-product",
          method: "POST",
          qa_mode: qa,
          data: orderData,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response?.data?.success || response?.data?.id) {
        setAlert({ type: "success", message: "Payment Successful!" });
        clearAll();
        const baseUrl = (redirectUrl || DEFAULT_REDIRECT).trim();
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set("email", email);
        if (url) window.location.href = url.toString();
      } else {
        setAlert({ type: "error", message: response?.data?.message || "Payment failed" });
      }
    } catch {
      setAlert({ type: "error", message: "Something went wrong" });
    } finally {
      setSubmitting(false);
    }
  };

  const [cardNumberReady, setCardNumberReady] = useState(false);
  const [cardExpiryReady, setCardExpiryReady] = useState(false);
  const [cardCvcReady, setCardCvcReady] = useState(false);
  const cardInputsReady = cardNumberReady && cardExpiryReady && cardCvcReady;

  return (
    <div className="container" style={{ maxWidth: 600 }}>
      <div className="mb-2" />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-center w-50">
          <p className="mb-0 fw-bold text-center" style={{ color: "#4b63b0" }}>Create Account</p>
          <small className=" text-muted  text-center">Contact Information</small>
        </div>
        <div className="text-center w-50">
          <p className="mb-0 fw-bold text-center" style={{ color: "#4b63b0" }}>Your Information</p>
          <small className="text-muted">Your Billing Information</small>
        </div>
      </div>
      <div className="mt-2">
        <div
          style={{
            height: 3,
            width: step === 1 ? "50%" : "100%",
            background: "#7487ff",
            borderRadius: 2,
            transition: "width .25s ease",
          }}
        />
      </div>

      {alert.type && (
        <div className={`alert ${alert.type === "error" ? "alert-danger" : "alert-success"} mt-3 mb-2`} role="alert">
          {alert.message}
        </div>
      )}

      {step === 1 && (
        <div className="bg-transparent rounded-3 p-0 mt-2">
          <div className="mb-4 mt-3">
            <input
              type="text"
              required
              placeholder="Full Name (Owner of the Account)"
              className="form-control osof-input py-3"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              name='fullname'
            />
          </div>
          <div className="mb-3">
            <input
              type="email"
              required
              placeholder="Email Address (Login/Password Recovery) "
              className="form-control osof-input py-3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              name="email"
            />
          </div>
          <div className="mb-3">
            <input
              name="phone"
              type="text"
              required
              placeholder="Phone Number"
              className="form-control osof-input py-3"
              ref={phoneRef}
            />
          </div>

          <div className="d-grid">
            <button type="button" className="btn fw-bold btn-primary py-3 fs-5 osof-btn" onClick={goStep2}>
              Go To Step #2
            </button>
          </div>

          <p className="text-center mt-2" style={{ fontSize: 12, color: "#6b7280" }}>
            * We Respect Your Privacy &amp; Information *
          </p>
        </div>
      )}

      {step === 2 && (
        <form className="bg-transparent rounded-3 p-0 mt-3" onSubmit={onSubmit}>
          <div className="mt-5">
            <div className="osof-table">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th className="fw-semibold">Item</th>
                    <th className="text-end fw-semibold">Quantity</th>
                    <th className="text-end fw-semibold">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const sub = parseSubscription(p?.subscription);
                    const now = getEffectivePrice(p);
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`product-${p.id}`}
                              checked={selectedMain.has(p.id)}
                              onChange={() => toggleMain(p.id)}
                              data-id={p.id}
                              data-price={now}
                              data-role-id={p.role_id}
                              data-is-subscription={sub?.is_subscription ? "true" : "false"}
                              data-trial-date={sub?.trial_dates || ""}
                              data-subscription-type={sub?.type_of_subscription || ""}
                            />
                            <label className="form-check-label ms-2" htmlFor={`product-${p.id}`} style={{ cursor: "pointer" }}>
                              {p.name}
                            </label>
                          </div>
                        </td>
                        <td className="text-end fw-bold">1</td>
                        <td className="text-end fw-bold">
                          {hasRealDiscount(p) && (
                            <span className="osof-old-price me-1">
                              ${Number.parseInt(p.price, 10)}
                            </span>
                          )}
                          <span className="text-primary">${now}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} className="fw-semibold">Order Total</td>
                    <td className="text-end fw-bold text-primary">${totalPrice}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {additionalProducts.length > 0 && (
              <div className="osof-offer-box mt-3" id="special-offer-checkbox">
                {additionalProducts.map((p) => {
                  const now = getEffectivePrice(p);
                  return (
                    <div key={p.id} className="mb-2">
                      <h3 className="h6">
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          id={`additional-${p.id}`}
                          checked={selectedUpsell.has(p.id)}
                          onChange={() => toggleUpsell(p.id)}
                          data-id={p.id}
                          data-price={now}
                        />
                        <label htmlFor={`additional-${p.id}`} className="ms-1" style={{ cursor: "pointer" }}>
                          Comprehensive Tax Analysis
                        </label>
                      </h3>
                      <p className="mb-0">
                        <strong style={{ color: "red" }}>
                          ONLY ${now} ONE TIME OFFER — Maximize Your Tax Savings!
                        </strong>
                        <br />
                        Let our tax strategists review your past returns and uncover customized strategies to help you save
                        more. Get your tax analysis report with custom tax strategies based on your unique situation. <br />
                        {hasRealDiscount(p) ? (
                          <>
                            This is usually <span className="osof-old-price">${Number.parseInt(p.price, 10)}</span>, but get it for just ${now} today.
                          </>
                        ) : (
                          <>This is ${now}.</>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="row g-3 mt-3">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">Card Number</label>
              <div className="osof-stripe">
                <CardNumberElement
                  options={stripeInputStyle}
                  onChange={(e) => setCardNumberReady(e.complete)}
                />
              </div>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label fw-semibold">Expiration</label>
              <div className="osof-stripe">
                <CardExpiryElement
                  options={stripeInputStyle}
                  onChange={(e) => setCardExpiryReady(e.complete)}
                />
              </div>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label fw-semibold">CVC</label>
              <div className="osof-stripe">
                <CardCvcElement
                  options={stripeInputStyle}
                  onChange={(e) => setCardCvcReady(e.complete)}
                />
              </div>
            </div>
          </div>

          <div className="mt-3">
            <button
              type="submit"
              className="btn btn-primary text-white w-100 d-flex align-items-center justify-content-center fw-bold fs-5 py-3 osof-btn"
              disabled={submitting || !cardInputsReady}
            >
              {submitting ? (
                <span className="osof-loader" />
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="26" height="26" fill="white" className="me-2">
                    <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm0 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zM17 18c-1.1 0-1.99.9-1.99 2S15.9 22 17 22s2-.9 2-2-.9-2-2-2zM7.04 11h9.91c.82 0 1.54-.5 1.85-1.25L21.79 4H6.21L5.26 2H1v2h3l3.6 7.59-1.35 2.45C5.08 14.9 5 15.15 5 15.5c0 .83.67 1.5 1.5 1.5H19v-2H7.42c-.12 0-.25-.08-.32-.19L8.1 13h8.88c1.38 0 2.63-.86 3.12-2.15L22 4H6.21l.83 1.59H21" />
                  </svg>
                  Complete Order
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function TwoStepOrderForm({
  productIds = [],
  additionalProductIds = [],
  testing = false,
  redirectUrl,
  testKey = import.meta.env.VITE_STRIPE_STAGING_KEY,
  liveKey = import.meta.env.VITE_STRIPE_PROD_KEY,
  proxyUrl,
}) {
  const qaModeURL =
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("qa_mode") === "on" ||
      new URLSearchParams(window.location.search).get("qa_mode") === "true" ||
      new URLSearchParams(window.location.search).get("testing") === "true");
  const useTest = Boolean(testing || qaModeURL);
  const activeKey = useTest ? testKey : liveKey;
  const stripePromise = useMemo(
    () => loadStripe(activeKey),
    [activeKey]
  );

  return (
    <Elements key={activeKey} stripe={stripePromise}>
      <TwoStepOrderFormInner
        productIds={productIds}
        additionalProductIds={additionalProductIds}
        redirectUrl={redirectUrl}
        qa_mode={useTest}
        proxyUrl={proxyUrl}
      />
    </Elements>
  );
}
